/**
 * voiceConsentApi.js
 * --------------------------------------------------------------
 * Thin client for the ParkAdda voice / IVR consent audit service.
 *
 * This is an MSG91 compliance requirement — every opt-in / opt-out
 * decision the user makes on a ParkAdda surface is stored server-side
 * with timestamp, text version, source and user-agent so that legal
 * can produce sample consent records on demand.
 *
 * See: https://msg91.com/help/voice/voice-consent
 */

import { API_BASES } from "../../config/apiConfig";

const VOICE_CONSENT_API_BASE = API_BASES.VOICE_CONSENT;

export const VOICE_CONSENT_SOURCE = {
  WEBSITE_BOOKING: "WEBSITE_BOOKING",
  WEBSITE_SIGNUP: "WEBSITE_SIGNUP",
  WEBSITE_SUPPORT: "WEBSITE_SUPPORT",
  B2B_APP: "B2B_APP",
  ADMIN_PANEL: "ADMIN_PANEL",
  IVR_SELF: "IVR_SELF",
};

const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    const msg = data.message || "Voice consent request failed";
    throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
  }
  return data.payload;
};

const jsonFetch = async (path, options = {}) => {
  const response = await fetch(`${VOICE_CONSENT_API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  return parseResponse(response);
};

export const voiceConsentApi = {
  /**
   * Persist a consent decision.  Safe to fire-and-forget from the UI,
   * but the caller should surface any thrown error so compliance gaps
   * don't fail silently.
   */
  record: (body) =>
    jsonFetch("/record/", { method: "POST", body: JSON.stringify(body) }),

  /**
   * Look up the current consent status for a phone number.
   * Returns { has_consent, valid, reason, source, version, valid_until, ... }.
   */
  status: (phone) =>
    jsonFetch("/status/?phone=" + encodeURIComponent(phone || "")),

  /**
   * User-initiated opt-out.
   */
  revoke: (body) =>
    jsonFetch("/revoke/", { method: "POST", body: JSON.stringify(body) }),
};
